import { Injectable } from '@angular/core';
import {AuthService} from "./auth.service";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Observable, throwError} from "rxjs";
import {catchError, map, switchMap, tap} from "rxjs/operators";
import {environment} from "../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class BillingProfileService {

  private apiUrl = environment.api.url;

  user: any;
  token: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) { }

  /**
   * Retrieves billing profile data from the backend API.
   * @returns Observable<{data: any, message: string}> that includes a response message and data.
   */
  getBillingProfile(): Observable<{data: any, message: string}> {
    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

    const url = `${this.apiUrl}/user-company-billing-profile?filter[user_id]=` + this.user.id;

    this.authService.token().subscribe(token => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}` // Use the token for authorization
      })
    };

    return this.http.get<{data: any, message: string}>(url, httpOptions).pipe(
      map(response => {
        if (!response.data) {
          // If the response object does not have a data property, throw an error
          throw new Error('Failed to retrieve billing profile: ' + response.message);
        }
        return {
          data: response.data,
          message: response.message
        };
      }),
      catchError(error => {
        return throwError(error); // Re-throw the error for further handling upstream
      })
    );
  }

  /**
   * Updates billing profile data in the backend API.
   * @param id The id of billing profile data to send
   * @param billingProfile The updated profile data to send
   * @returns Observable<{data: any, message: string}>
   */
  updateBillingProfile(id: number, billingProfile: any): Observable<{data: any, message: string}> {
    const url = `${this.apiUrl}/user-company-billing-profile/${id}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      })
    };

    return this.http.put<{data: any, message: string}>(url, billingProfile, httpOptions).pipe(
      map(response => {
        if (!response.data) {
          throw new Error('Failed to update billing profile: ' + response.message);
        }
        return {
          data: response.data,
          message: response.message
        };
      }),
      catchError(error => {
        return throwError(error);
      })
    );
  }

  /**
   * Submits a new billing profile to the backend.
   * @param billingProfile The billing profile data to submit
   * @returns Observable<any> The response from the server
   */
  submitBillingProfile(billingProfile: any): Observable<any> {
    const url = `${this.apiUrl}/user-company-billing-profile`;
    return this.authService.token().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // Assume token is fetched from authService
        });
        return this.http.post(url, billingProfile, { headers }).pipe(
          map(response => {
            return response;
          }),
          catchError(error => {
            return throwError(error);
          })
        );
      })
    );
  }

  deleteBillingProfile(id: number): Observable<void> {
    return this.authService.token().pipe(
      tap(token => this.token = token),
      switchMap(() => {
        const url = `${this.apiUrl}/user-company-billing-profile/${id}`;
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}` // Use the token for authorization
          })
        };

        return this.http.delete<void>(url, httpOptions).pipe(
          catchError(error => {
            return throwError(error); // Re-throw the error for further handling upstream
          })
        );
      })
    );
    }

}
